import { yupResolver } from '@hookform/resolvers/yup';
import TextField from '@material-ui/core/TextField';
import { FormControl, IconButton, Link, Typography } from '@material-ui/core';
import { Link as RouterLink } from 'react-router-dom';

import { makeStyles } from '@material-ui/core/styles';
import React, { useContext, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import LoginButton from 'components/Buttons/LoginButton';
import { CoreUnlockIcon, CoreUserIcon } from 'components/Icons';
import { notify } from 'components/Notifier';
import { AppContext } from 'context';
import MemoEyeCloseIcon from 'components/Icons/EyeCloseIcon';
import MemoEyeOpenIcon from 'components/Icons/EyeOpenIcon';

const useStyles = makeStyles({
  form: {
    display: 'flex',
    flexFlow: 'column',

    '& > *': {
      marginBottom: '1rem',
    },

    '& > h2': {
      marginTop: 0,
    },
  },
  subtitle: {
    marginBottom: '2em',
  },
  logo: {
    width: '12rem',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  fieldLine: {
    display: 'flex',
    alignItems: 'center',
    '& > svg': {
      marginTop: '10px',
      marginRight: '10px',
    },
    '& > div': {
      minWidth: '260px',
    },
  },
  forgotPassword: {
    textAlign: 'right',
  },
  formControl: {
    width: '100%',
  },
  eyeButton: {
    width: '50px',
    height: '50px',
    position: 'absolute',
    marginTop: '7px',
    marginLeft: '85%',
  },
});

const LoginForm: React.FC = () => {
  const context = useContext(AppContext);
  const isEdgeBrowser = context?.isEdgeBrowser?.() ?? false;
  const classes = useStyles();
  const { t } = useTranslation('translation', { keyPrefix: 'loginForm' });
  const [showPassword, setShowPassword] = useState(false);

  const schema = yup.object().shape({
    email: yup.string().email(t('validation.invalidEmail')).required(t('validation.requiredEmail')),
    password: yup.string().required(t('validation.requiredPassword')),
  });

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      email: '',
      password: '',
    },
  });
  const onSubmit = async (values: any) => {
    try {
      await context.login(values);
    } catch (e) {
      // @ts-ignore
      if (e.status === 401) {
        // @ts-ignore
        if (e.response.body.translationKey) {
          notify({
            // @ts-ignore
            message: t(e.response.body.translationKey, e.response.body.additionalData) + ' ' + t('contactAdmin'),
            severity: 'error',
          });
        } else {
          notify({ message: t('incorrectLogin'), severity: 'error' });
        }
      } else {
        notify({ message: t('genericProblem'), severity: 'error' });
      }
    }
  };

  return (
    <form className={classes.form} onSubmit={handleSubmit(onSubmit)}>
      <Typography className={classes.subtitle}>{t('subtitle')}</Typography>
      <div className={classes.fieldLine}>
        <CoreUserIcon />
        <Controller
          name="email"
          control={control}
          render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
            <TextField
              fullWidth
              id="email"
              label={t('email')}
              autoComplete="username"
              error={!!error}
              helperText={error?.message}
              inputRef={ref}
              {...inputProps}
            />
          )}
        />
      </div>
      <div className={classes.fieldLine}>
        <CoreUnlockIcon />
        <FormControl className={classes.formControl}>
          <Controller
            name="password"
            control={control}
            render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
              <>
                <TextField
                  fullWidth
                  id="password"
                  label={t('password')}
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  error={!!error}
                  helperText={error?.message}
                  inputRef={ref}
                  {...inputProps}
                />
                {!isEdgeBrowser && (
                  <IconButton
                    className={classes.eyeButton}
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    onMouseDown={event => event.preventDefault()}
                  >
                    {showPassword ? <MemoEyeCloseIcon /> : <MemoEyeOpenIcon />}
                  </IconButton>
                )}
              </>
            )}
          />
        </FormControl>
      </div>
      <Link className={classes.forgotPassword} component={RouterLink} to="/reset-password">
        {t('forgottenPasswordLink')}
      </Link>
      <LoginButton disabled={isSubmitting}>{t('connect')}</LoginButton>
    </form>
  );
};

export default LoginForm;
